import { Box, Typography, keyframes } from "@mui/material";
import { MdError, MdWarning, MdEmail } from "react-icons/md";
import { useLicenseCountdown } from "../hooks/useLicenseCountdown";

// Pulse animation for critical/expired states
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// Gradient animation for background
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/**
 * LicenseExpiredOverlay
 * 
 * Full-screen blocking overlay when license is expired.
 * Completely blocks the display content - customer cannot use the system.
 */
export function LicenseExpiredOverlay({ customerName, contactEmail }) {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(-45deg, #1a1a2e, #16213e, #0f3460, #1a1a2e)",
        backgroundSize: "400% 400%",
        animation: `${gradientShift} 15s ease infinite`,
        color: "white",
        textAlign: "center",
        padding: "40px",
      }}
    >
      {/* Error icon with pulse */}
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          bgcolor: "rgba(220, 38, 38, 0.2)",
          display: "grid",
          placeItems: "center",
          mb: 4,
          animation: `${pulse} 2s infinite`,
        }}
      >
        <MdError size={64} color="#ef4444" />
      </Box>

      {/* Main message */}
      <Typography
        variant="h2"
        sx={{
          fontWeight: 800,
          mb: 2,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          textShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        License Expired
      </Typography>

      <Typography
        variant="h5"
        sx={{
          mb: 4,
          opacity: 0.9,
          maxWidth: 600,
          fontSize: "clamp(1rem, 2vw, 1.5rem)",
        }}
      >
        The license for {customerName || "this system"} has expired.
        <br />
        Display service has been suspended.
      </Typography>

      {/* Contact info */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.2)",
          maxWidth: 500,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <MdEmail size={24} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Contact Administrator
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Please contact your system administrator to renew the license and restore service.
        </Typography>
        {contactEmail && (
          <Typography
            variant="body2"
            sx={{ mt: 2, fontFamily: "monospace", opacity: 0.7 }}
          >
            {contactEmail}
          </Typography>
        )}
      </Box>

      {/* Branding */}
      <Typography
        sx={{
          position: "absolute",
          bottom: 20,
          opacity: 0.5,
          fontSize: "0.9rem",
        }}
      >
        FidsNet Flight Information Display System
      </Typography>
    </Box>
  );
}

/**
 * LicenseWarningBanner
 * 
 * Small banner at top/bottom of screen when license is expiring soon.
 * Does NOT block the display - just shows a warning with live countdown.
 * Uses server-synced timing for accurate countdown across all displays.
 */
export function LicenseWarningBanner({
  daysRemaining,
  expiresOn,
  expiresAt = null,
  msRemaining = null,
  serverNow = null,
  isCritical = false,
  position = "bottom",
}) {
  // Use live countdown with server-synced timing for accurate cross-display sync
  const countdown = useLicenseCountdown(expiresOn, expiresAt, msRemaining, serverNow);
  
  const bgColor = isCritical
    ? "linear-gradient(90deg, #dc2626 0%, #ef4444 100%)"
    : "linear-gradient(90deg, #d97706 0%, #f59e0b 100%)";

  return (
    <Box
      sx={{
        position: "fixed",
        [position]: 0,
        left: 0,
        right: 0,
        zIndex: 9998,
        py: 1,
        px: 2,
        background: bgColor,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        fontSize: "clamp(12px, 1.2vw, 18px)",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        animation: isCritical ? `${pulse} 2s infinite` : "none",
      }}
    >
      <MdWarning size={20} />
      <span>
        License expires in{" "}
        <Box
          component="span"
          sx={{
            fontFamily: "monospace",
            bgcolor: "rgba(0,0,0,0.2)",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 700,
          }}
        >
          {countdown.formatted}
        </Box>
        {" "}— Contact administrator to renew
      </span>
    </Box>
  );
}

export default LicenseExpiredOverlay;
